import React, {forwardRef, useMemo} from "react";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import Button from "../../../../../../../../components/base/button/button";
import classnames from "classnames";
import {PanelFooter} from "../../../../../../../../components/layout";
import {NewBulkPriceChangeSteps} from "../../index";

const NewBulkPriceChangeViewBottomActionBar = forwardRef(({
                                                              state,
                                                              setStep,
                                                              updatingStep,
                                                          }, ref) => {

    /**@type {boolean}*/
    const show = useMemo(() => {
        let show = false
        switch (state.step) {
            case NewBulkPriceChangeSteps.productSelection:
                if (state.totalAffectedParts > 0)
                    show = true;
                break;
            case NewBulkPriceChangeSteps.priceLevelSelection:
                if (state.affectedPriceLevels > 0)
                    show = true;
                break;
            case NewBulkPriceChangeSteps.changePrice:
                if (state.requestedAmount !== 0)
                    show = true;
                break;
            default :
                show = true;
                break;
        }
        return show;
    }, [state.step, state.totalAffectedParts, state.affectedPriceLevels, state.requestedAmount])

    /**@type {string}*/
    const title = useMemo(() => {
        switch (state.step) {
            case NewBulkPriceChangeSteps.productSelection:
                return `${state.totalAffectedParts} Product${state.totalAffectedParts !== 1 ? 's' : ''} Selected`;
            case NewBulkPriceChangeSteps.priceLevelSelection:
                return `${state.affectedPriceLevels} Price Level${state.affectedPriceLevels !== 1 ? 's' : ''} Selected`;
            case NewBulkPriceChangeSteps.changePrice:
                return `${!state.isPercentage
                    ? `${state.requestedAmount > 0 ? '+' : '-'}` + formatMoney(Math.abs(state.requestedAmount))
                    : `${state.requestedAmount > 0 ? '+' : ''}` + state.requestedAmount?.toFixed(2) + '%'}`
            case NewBulkPriceChangeSteps.reviewAndFinalize:
                return `${!state.isPercentage
                        ? `${state.requestedAmount > 0 ? '+' : '-'}` + formatMoney(Math.abs(state.requestedAmount))
                        : `${state.requestedAmount > 0 ? '+' : ''}` + state.requestedAmount?.toFixed(2) + '%'}` +
                    ' to ' +
                    `${state.affectedPriceLevels} Price level${state.affectedPriceLevels === 1 ? '' : 's'}` +
                    ' of ' +
                    `${state.totalAffectedParts} Product${state.totalAffectedParts === 1 ? '' : 's'}`
            default:
                return "";
        }
    }, [state.affectedPriceLevels, state.isPercentage, state.requestedAmount, state.step, state.totalAffectedParts])

    /**@type {boolean}*/
    const showPreviousStep = useMemo(() =>
            ![
                NewBulkPriceChangeSteps.productSelection,
                NewBulkPriceChangeSteps.finalized,
            ].includes(state.step),
        [state.step])

    /**@type {string}*/
    const previousStepTitle = useMemo(() => {
        if (updatingStep)
            return "Processing..."
        switch (state.step) {
            case NewBulkPriceChangeSteps.priceLevelSelection:
                return "Back to product selection"
            case NewBulkPriceChangeSteps.changePrice:
                return "Back to price-level selection"
            case NewBulkPriceChangeSteps.reviewAndFinalize:
                return "Back to change price section"
            default:
                return '';

        }
    }, [state.step, updatingStep])


    /**@type {string}*/
    const nextStepTitle = useMemo(() => {
        if (updatingStep) {
            return "Proceeding..."
        }
        if (state.step === NewBulkPriceChangeSteps.reviewAndFinalize) {
            return "Finalize";
        }
        return "Next Step";
    }, [state.step, updatingStep])

    /**
     * Navigates the user to the previous step of the request.
     * @return {Function | undefined}
     */
    const setPrevStep = () => {
        let newStep;
        switch (state.step) {
            case NewBulkPriceChangeSteps.priceLevelSelection:
                newStep = NewBulkPriceChangeSteps.productSelection;
                break;
            case NewBulkPriceChangeSteps.changePrice:
                newStep = NewBulkPriceChangeSteps.priceLevelSelection;
                break;
            case NewBulkPriceChangeSteps.reviewAndFinalize:
                newStep = NewBulkPriceChangeSteps.changePrice;
                break;
            default:
                break;
        }
        if (newStep) {
            setStep(newStep);
        }
    }

    /**
     * Navigates the user to the next step of the request.
     * @return {Function | undefined}
     */
    const setNextStep = () => {
        let newStep;
        switch (state.step) {
            case NewBulkPriceChangeSteps.productSelection:
                newStep = NewBulkPriceChangeSteps.priceLevelSelection;
                break;
            case NewBulkPriceChangeSteps.priceLevelSelection:
                newStep = NewBulkPriceChangeSteps.changePrice;
                break;
            case NewBulkPriceChangeSteps.changePrice:
                newStep = NewBulkPriceChangeSteps.reviewAndFinalize;
                break;
            case NewBulkPriceChangeSteps.reviewAndFinalize:
                newStep = NewBulkPriceChangeSteps.finalized;
                break;
            default:
                break;
        }
        if (newStep) {
            setStep(newStep);
        }
    }

    return (
        <>
            {
                show &&
                <PanelFooter>
                    <div
                        className={'bottom-action-bar'}
                        // ref={ref}
                        // style={{visibility: !show ? 'hidden' : 'unset'}}
                    >
                        <div className={classnames('bottom-action-bar-content', {'with-previous-step': showPreviousStep})}>
                            <div className={'d-flex flex-column align-items-start'}>
                                <p>
                                    {title}
                                </p>
                                {
                                    showPreviousStep &&
                                    <div className={'previous-step'}>
                                        <IconButton
                                            disabled={updatingStep}
                                            onPressed={setPrevStep}
                                            color={'paper.secondary'}
                                        >
                                            <ArrowBack className='icon small'/>
                                        </IconButton>
                                        <p>
                                            {previousStepTitle}
                                        </p>
                                    </div>
                                }
                            </div>
                            <Button
                                disabled={updatingStep}
                                onClick={setNextStep}
                                variant={'contained'}
                                color={'primary'}
                            >
                                {nextStepTitle}
                            </Button>
                        </div>
                    </div>
                </PanelFooter>
            }
        </>
    );
})

export default NewBulkPriceChangeViewBottomActionBar;
